import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import StatCard from '../../components/ui/StatCard';
import AdminLayout from '../../components/layout/AdminLayout';

const AdminDashboard = () => {
  // جلب إحصائيات عامة
  const { data: stats, isLoading: statsLoading } = useQuery(
    'admin-stats',
    () => api.get('/admin/stats').then(res => res.data.data),
    {
      onError: (error) => {
        console.error('خطأ في جلب الإحصائيات:', error);
      }
    }
  );

  // جلب البائعين المعلقين
  const { data: pendingVendors, isLoading: vendorsLoading } = useQuery(
    'pending-vendors',
    () => api.get('/admin/vendors/pending').then(res => res.data.data),
    {
      onError: (error) => {
        console.error('خطأ في جلب البائعين المعلقين:', error);
      }
    }
  );

  const handleVendorAction = async (vendorId, action) => {
    try {
      await api.put(`/admin/vendors/${vendorId}/${action}`);
      // إعادة تحميل البيانات
      window.location.reload();
    } catch (error) {
      console.error(`خطأ في ${action} البائع:`, error);
    }
  };

  if (statsLoading) {
    return (
      <AdminLayout>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              لوحة الإدارة
            </h1>
            <p className="mt-2 text-gray-600">
              مرحباً بك في لوحة إدارة أمازول
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            إجراءات سريعة
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/admin/users"
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <UsersIcon className="w-8 h-8 text-blue-600 ml-3" />
                <div>
                  <h3 className="font-medium text-gray-900">إدارة المستخدمين</h3>
                  <p className="text-sm text-gray-500">عرض وإدارة المستخدمين</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/vendors"
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <BuildingStorefrontIcon className="w-8 h-8 text-green-600 ml-3" />
                <div>
                  <h3 className="font-medium text-gray-900">إدارة البائعين</h3>
                  <p className="text-sm text-gray-500">الموافقة على البائعين</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/products"
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <ShoppingBagIcon className="w-8 h-8 text-purple-600 ml-3" />
                <div>
                  <h3 className="font-medium text-gray-900">إدارة المنتجات</h3>
                  <p className="text-sm text-gray-500">مراجعة المنتجات</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/orders"
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <ShoppingBagIcon className="w-8 h-8 text-indigo-600 ml-3" />
                <div>
                  <h3 className="font-medium text-gray-900">إدارة الطلبات</h3>
                  <p className="text-sm text-gray-500">متابعة الطلبات</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/categories"
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <CogIcon className="w-8 h-8 text-orange-600 ml-3" />
                <div>
                  <h3 className="font-medium text-gray-900">إدارة الفئات</h3>
                  <p className="text-sm text-gray-500">إضافة وتعديل الفئات</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/reviews"
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <ChartBarIcon className="w-8 h-8 text-yellow-600 ml-3" />
                <div>
                  <h3 className="font-medium text-gray-900">إدارة المراجعات</h3>
                  <p className="text-sm text-gray-500">مراجعة التقييمات</p>
                </div>
              </div>
            </Link>

            <Link
              to="/admin/settlements"
              className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <ChartBarIcon className="w-8 h-8 text-teal-600 ml-3" />
                <div>
                  <h3 className="font-medium text-gray-900">التسويات المالية</h3>
                  <p className="text-sm text-gray-500">إدارة مدفوعات البائعين</p>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              الإحصائيات العامة
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="إجمالي المستخدمين"
                value={stats.users?.total || 0}
                icon={UsersIcon}
                color="blue"
              />
              <StatCard
                title="البائعين النشطين"
                value={stats.vendors?.active || 0}
                icon={BuildingStorefrontIcon}
                color="green"
              />
              <StatCard
                title="إجمالي المنتجات"
                value={stats.products?.total || 0}
                icon={ShoppingBagIcon}
                color="purple"
              />
              <StatCard
                title="إجمالي الطلبات"
                value={stats.orders?.total || 0}
                icon={ShoppingBagIcon}
                color="indigo"
              />
              <StatCard
                title="الطلبات المعلقة"
                value={stats.orders?.pending || 0}
                icon={ShoppingBagIcon}
                color="yellow"
              />
              <StatCard
                title="الطلبات المكتملة"
                value={stats.orders?.completed || 0}
                icon={ShoppingBagIcon}
                color="green"
              />
              <StatCard
                title="إجمالي المبيعات"
                value={`${(stats.sales?.total || 0).toLocaleString()} جنيه`}
                icon={ChartBarIcon}
                color="orange"
              />
              <StatCard
                title="مبيعات اليوم"
                value={`${(stats.sales?.today || 0).toLocaleString()} جنيه`}
                icon={ChartBarIcon}
                color="teal"
              />
            </div>
          </div>
        )}

        {/* Pending Vendors */}
        {pendingVendors && pendingVendors.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              البائعين في انتظار الموافقة ({pendingVendors.length})
            </h2>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        اسم المتجر
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        صاحب المتجر
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الفئة
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        تاريخ التقديم
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الإجراءات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pendingVendors.map((vendor) => (
                      <tr key={vendor.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {vendor.store_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {vendor.user?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {vendor.user?.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            {vendor.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(vendor.created_at).toLocaleDateString('ar-SA')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2 space-x-reverse">
                            <button
                              onClick={() => handleVendorAction(vendor.id, 'approve')}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                            >
                              <CheckCircleIcon className="w-4 h-4 ml-1" />
                              موافقة
                            </button>
                            <button
                              onClick={() => handleVendorAction(vendor.id, 'reject')}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <XCircleIcon className="w-4 h-4 ml-1" />
                              رفض
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {vendorsLoading && (
          <div className="text-center py-8">
            <LoadingSpinner size="md" />
            <p className="mt-2 text-gray-600">جاري تحميل البائعين المعلقين...</p>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
};

export default AdminDashboard;